import { GripVertical, Minus, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createApiUrl } from '../../../config/api';
import { usePlotViewStore } from '../../../stores/plotViewStore';
import PlotlyViewer from './PlotlyViewer';

interface PlotItem {
  file_name: string;
  file_type: string;
  plot_json?: string;
}

interface PlotGridViewProps {
  plots: PlotItem[];
  tabId: string;
  sessionId?: string;
  userID?: string;
}

const PlotGridView: React.FC<PlotGridViewProps> = ({ plots, tabId, sessionId, userID }) => {
  const {
    getTabLayout,
    setGridConfig,
    setGridAssignment,
    initializeTabLayout,
  } = usePlotViewStore();

  const layout = getTabLayout(tabId);
  const { rows, cols } = layout.gridConfig;

  const [draggedPlotId, setDraggedPlotId] = useState<string | null>(null);
  const [dragOverCell, setDragOverCell] = useState<number | null>(null);

  // Initialize layout when plots change
  useEffect(() => {
    if (plots.length > 0) {
      initializeTabLayout(
        tabId,
        plots.map((p) => p.file_name)
      );
    }
  }, [tabId, plots, initializeTabLayout]);

  // Build image URLs for non-Plotly plots
  const plotImageUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    plots.forEach((plot) => {
      if (!plot.plot_json && sessionId && userID) {
        urls[plot.file_name] = createApiUrl(
          `/file_handling/users/${userID}/sessions/${sessionId}/files/processed/file_types/plot/${encodeURIComponent(plot.file_name)}`
        );
      }
    });
    return urls;
  }, [plots, sessionId, userID]);

  // Create grid cells
  const totalCells = rows * cols;
  const cells = Array.from({ length: totalCells }, (_, i) => i);

  // Find plot assigned to each cell
  const getPlotForCell = useCallback(
    (cellIndex: number): PlotItem | undefined => {
      const plotId = Object.entries(layout.gridAssignments).find(
        ([_, idx]) => idx === cellIndex
      )?.[0];
      return plots.find((p) => p.file_name === plotId);
    },
    [layout.gridAssignments, plots]
  );

  // Get unassigned plots (for the sidebar/pool)
  const unassignedPlots = useMemo(() => {
    const assignedPlotIds = new Set(Object.keys(layout.gridAssignments));
    return plots.filter((p) => !assignedPlotIds.has(p.file_name));
  }, [plots, layout.gridAssignments]);

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, plotId: string) => {
    setDraggedPlotId(plotId);
    e.dataTransfer.setData('text/plain', plotId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  // Handle drag over cell
  const handleDragOver = useCallback((e: React.DragEvent, cellIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell(cellIndex);
  }, []);

  // Handle drag leave
  const handleDragLeave = useCallback(() => {
    setDragOverCell(null);
  }, []);

  // Handle drop on cell
  const handleDrop = useCallback(
    (e: React.DragEvent, cellIndex: number) => {
      e.preventDefault();
      const plotId = e.dataTransfer.getData('text/plain');

      if (!plotId) return;

      // Check if target cell already has a plot
      const existingPlotEntry = Object.entries(layout.gridAssignments).find(
        ([_, idx]) => idx === cellIndex
      );

      if (existingPlotEntry) {
        const [existingPlotId] = existingPlotEntry;
        // Swap: move existing plot to the source cell
        const sourceCell = layout.gridAssignments[plotId];
        if (sourceCell !== undefined) {
          setGridAssignment(tabId, existingPlotId, sourceCell);
        } else {
          // If dragged from unassigned pool, remove existing from grid
          // by assigning to a high cell number (effectively unassigning)
          const nextFreeCell = Math.max(...Object.values(layout.gridAssignments), totalCells) + 1;
          setGridAssignment(tabId, existingPlotId, nextFreeCell);
        }
      }

      setGridAssignment(tabId, plotId, cellIndex);
      setDraggedPlotId(null);
      setDragOverCell(null);
    },
    [layout.gridAssignments, tabId, setGridAssignment, totalCells]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedPlotId(null);
    setDragOverCell(null);
  }, []);

  // Render a plot in a cell
  const renderPlotInCell = (plot: PlotItem) => {
    const displayName = plot.file_name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ').slice(0, 40);

    return (
      <div
        key={plot.file_name}
        draggable
        onDragStart={(e) => handleDragStart(e, plot.file_name)}
        onDragEnd={handleDragEnd}
        className={`h-full w-full bg-white rounded-lg border shadow-sm overflow-hidden transition-opacity cursor-grab active:cursor-grabbing ${
          draggedPlotId === plot.file_name ? 'opacity-50' : ''
        }`}
      >
        {plot.plot_json ? (
          <PlotlyViewer plotJson={plot.plot_json} fileName={displayName} />
        ) : plotImageUrls[plot.file_name] ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-50 p-2">
            <img
              src={plotImageUrls[plot.file_name]}
              alt={plot.file_name}
              className="max-w-full max-h-full object-contain"
              loading="lazy"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            <span className="text-gray-500 text-sm">No plot data</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col p-4 overflow-hidden">
      {/* Configuration panel */}
      <div className="flex items-center gap-6 mb-4 px-4 py-2.5 bg-white rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Rows</label>
          <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200">
            <button
              onClick={() => setGridConfig(tabId, Math.max(1, rows - 1), cols)}
              disabled={rows <= 1}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-l-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Minus size={14} />
            </button>
            <span className="w-8 text-center text-sm font-medium text-gray-700">{rows}</span>
            <button
              onClick={() => setGridConfig(tabId, rows + 1, cols)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-r-lg transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="w-px h-6 bg-gray-200" />

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Columns</label>
          <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200">
            <button
              onClick={() => setGridConfig(tabId, rows, Math.max(1, cols - 1))}
              disabled={cols <= 1}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-l-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Minus size={14} />
            </button>
            <span className="w-8 text-center text-sm font-medium text-gray-700">{cols}</span>
            <button
              onClick={() => setGridConfig(tabId, rows, cols + 1)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-r-lg transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="w-px h-6 bg-gray-200" />

        <div className="text-sm text-gray-400">
          {plots.length} plot{plots.length !== 1 ? 's' : ''} · {totalCells} cell{totalCells !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Grid layout with overflow scroll */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div
          className="grid gap-4 h-full"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(250px, 1fr))`,
            gridTemplateRows: `repeat(${rows}, minmax(200px, 1fr))`,
          }}
        >
          {cells.map((cellIndex) => {
            const plot = getPlotForCell(cellIndex);
            const isDragOver = dragOverCell === cellIndex;

            return (
              <div
                key={cellIndex}
                onDragOver={(e) => handleDragOver(e, cellIndex)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, cellIndex)}
                className={`rounded-lg border-2 border-dashed transition-all overflow-hidden ${
                  isDragOver
                    ? 'border-blue-400 bg-blue-50'
                    : plot
                    ? 'border-transparent'
                    : 'border-gray-200 bg-gray-50/50'
                }`}
              >
                {plot ? (
                  renderPlotInCell(plot)
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    {isDragOver ? 'Drop here' : 'Empty cell'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Unassigned plots pool (if any) */}
      {unassignedPlots.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm font-medium text-gray-700 mb-2">
            Unassigned Plots ({unassignedPlots.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {unassignedPlots.map((plot) => (
              <div
                key={plot.file_name}
                draggable
                onDragStart={(e) => handleDragStart(e, plot.file_name)}
                onDragEnd={handleDragEnd}
                className={`px-3 py-2 bg-white border rounded-lg cursor-grab active:cursor-grabbing flex items-center gap-2 hover:shadow-md transition-shadow ${
                  draggedPlotId === plot.file_name ? 'opacity-50' : ''
                }`}
              >
                <GripVertical size={14} className="text-gray-400" />
                <span className="text-sm text-gray-700">
                  {plot.file_name.replace(/\.[^/.]+$/, '').slice(0, 30)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotGridView;
